import { IJsonModel, Model, Actions, DockLocation } from 'flexlayout-react';

/**
 * Generate a stable UUID for a widget tab instance.
 * Used as the FlexLayout tab id, which downstream becomes the widget `instanceId`
 * and the key for per-instance state (group, controls, settings).
 */
export function generateTabId(): string {
  // crypto.randomUUID is supported in all modern browsers and recent Node.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Defensive fallback — extremely unlikely path in supported environments.
  return 'tab-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/**
 * Walk a FlexLayout model and assign a stable UUID to any tab missing an `id`.
 * Returns true if any tab was mutated, so callers can persist the migrated model.
 */
export function ensureStableTabIds(model: IJsonModel): boolean {
  let mutated = false;
  const visit = (node: unknown) => {
    if (!node || typeof node !== 'object') return;
    const n = node as { type?: string; id?: string; children?: unknown[] };
    if (n.type === 'tab' && !n.id) {
      n.id = generateTabId();
      mutated = true;
    }
    if (Array.isArray(n.children)) {
      n.children.forEach(visit);
    }
  };
  visit(model.layout);
  (model.borders ?? []).forEach(visit);
  return mutated;
}

/**
 * Add a widget to the layout programmatically
 * @param model - FlexLayout model instance
 * @param widgetId - Widget ID from registry
 * @param widgetName - Display name for the tab
 * @param config - Optional widget configuration
 */
export function addWidgetToLayout(
  model: Model,
  widgetId: string,
  widgetName: string,
  config?: Record<string, unknown>
): void {
  // CENTER docking only works against a TabSet — dropping CENTER on the root
  // row is a no-op. Prefer the user's active tabset; fall back to the first
  // tabset; if the layout has no tabsets yet, dock a new one to the right of
  // the root row instead.
  const targetTabset =
    model.getActiveTabset() ?? model.getFirstTabSet();
  const tab = {
    type: 'tab',
    id: generateTabId(),
    component: widgetId,
    name: widgetName,
    config: config || {},
  };
  if (targetTabset) {
    model.doAction(
      Actions.addNode(tab, targetTabset.getId(), DockLocation.CENTER, -1, true)
    );
  } else {
    model.doAction(
      Actions.addNode(tab, model.getRoot().getId(), DockLocation.RIGHT, -1, true)
    );
  }
}

/**
 * Create a default FlexLayout model with no widgets
 * Apps can use this as a starting point
 */
export function createDefaultModel(): IJsonModel {
  return {
    global: {
      tabEnableClose: true,
      tabEnableRename: false,
      tabSetEnableMaximize: true,
      tabSetEnableDrop: true,
      tabSetEnableDeleteWhenEmpty: true,
      tabSetEnableDrag: true,
      tabSetEnableDivide: true,
      borderEnableDrop: true,
    },
    borders: [],
    layout: {
      type: 'row',
      weight: 100,
      children: [],
    },
  };
}

/**
 * Create a model with example widgets pre-loaded
 * Useful for demos and testing
 */
export function createExampleModel(): IJsonModel {
  return {
    global: {
      tabEnableClose: true,
      tabEnableRename: false,
      tabSetEnableMaximize: true,
      tabSetEnableDrop: true,
      tabSetEnableDeleteWhenEmpty: true,
      tabSetEnableDrag: true,
      tabSetEnableDivide: true,
      borderEnableDrop: true,
    },
    borders: [],
    layout: {
      type: 'row',
      weight: 100,
      children: [
        {
          type: 'tabset',
          weight: 50,
          children: [
            {
              type: 'tab',
              name: 'Chart',
              component: 'chart-widget',
              config: {},
            },
          ],
        },
        {
          type: 'tabset',
          weight: 50,
          children: [
            {
              type: 'tab',
              name: 'Positions',
              component: 'positions-widget',
              config: {},
            },
            {
              type: 'tab',
              name: 'Orders',
              component: 'orders-widget',
              config: {},
            },
          ],
        },
      ],
    },
  };
}

/**
 * Create a trading-focused layout with chart, positions, orders, and metrics
 */
export function createTradingLayout(): IJsonModel {
  return {
    global: {
      tabEnableClose: true,
      tabEnableRename: false,
      tabSetEnableMaximize: true,
      tabSetEnableDrop: true,
      tabSetEnableDeleteWhenEmpty: true,
      tabSetEnableDrag: true,
      tabSetEnableDivide: true,
      borderEnableDrop: true,
    },
    borders: [],
    layout: {
      type: 'row',
      weight: 100,
      children: [
        {
          type: 'column',
          weight: 70,
          children: [
            {
              type: 'tabset',
              weight: 60,
              children: [
                {
                  type: 'tab',
                  name: 'Chart',
                  component: 'chart-widget',
                  config: {},
                },
              ],
            },
            {
              type: 'tabset',
              weight: 40,
              children: [
                {
                  type: 'tab',
                  name: 'Orders',
                  component: 'orders-widget',
                  config: {},
                },
              ],
            },
          ],
        },
        {
          type: 'column',
          weight: 30,
          children: [
            {
              type: 'tabset',
              weight: 40,
              children: [
                {
                  type: 'tab',
                  name: 'Metrics',
                  component: 'metrics-widget',
                  config: {},
                },
              ],
            },
            {
              type: 'tabset',
              weight: 60,
              children: [
                {
                  type: 'tab',
                  name: 'Positions',
                  component: 'positions-widget',
                  config: {},
                },
              ],
            },
          ],
        },
      ],
    },
  };
}

/**
 * Save layout model to localStorage
 */
export function saveLayoutToLocalStorage(key: string, model: IJsonModel): void {
  try {
    localStorage.setItem(key, JSON.stringify(model));
  } catch (error) {
    console.error('Failed to save layout to localStorage:', error);
  }
}

/**
 * Load layout model from localStorage
 * Returns undefined if not found or invalid
 */
export function loadLayoutFromLocalStorage(key: string): IJsonModel | undefined {
  try {
    const data = localStorage.getItem(key);
    if (!data) return undefined;
    return JSON.parse(data) as IJsonModel;
  } catch (error) {
    console.error('Failed to load layout from localStorage:', error);
    return undefined;
  }
}

/**
 * Get layout storage key for a specific app/user
 */
export function getLayoutStorageKey(appName: string, userId?: string): string {
  return userId ? `pulse-layout-${appName}-${userId}` : `pulse-layout-${appName}`;
}
